/**
 * Konzept-Schritt v1 im Seitenpanel (Wireframe 19.10.2026, #gliederung #konzept #kapitel):
 * die gelesene Gliederung zum Bestätigen, der Reiter „Konzept“ mit Kapitelstatus, schlichter
 * Vorschau mit gelben Platzhaltern und „Nächster Platzhalter“, und die Knöpfe für die .docx.
 *
 * Gezeigt wird, was der Server ablegt (`concept.outline`, `concept.document`, tri-server
 * docs/tender-concept-step.md). Die Vorschau ist der geschriebene Stand, nicht die Datei nach einer
 * Bearbeitung in Word — so beschriftet. „In Word öffnen“ gibt es nur, wenn die Ablage SharePoint ist
 * (`webUrl`, Word Online); bei S3 bleibt „.docx laden“ über die vorhandene API (entschieden 20.10.2026).
 */
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAtom } from "jotai";
import { ChevronLeft, ChevronRight, Download, ExternalLink, FileText } from "lucide-react";

import { Button } from "@/components/ui/button";
import { userAPI } from "../../UserAPI";
import { authenticationAtom } from "../../State";
import type {
  TenderConceptDocument,
  TenderConceptOutline,
  TenderConceptSource,
  TenderPanelConcept,
} from "../../TenderSearchProfileAPI";

const PLACEHOLDER = /(\{\{[A-Za-zÄÖÜäöüß_]+\}\})/g;

export const formatConceptSource = (source: TenderConceptSource): string =>
  source.page ? `${source.file}, S. ${source.page}` : source.file;

const SourceChips = ({ sources }: { sources?: TenderConceptSource[] }) =>
  sources?.length ? (
    <span className="mt-1 flex flex-wrap gap-1">
      {sources.map((source, index) => (
        <span key={index} className="rounded border border-border bg-muted px-1.5 py-0.5 text-[11px] text-muted-foreground">
          {formatConceptSource(source)}
        </span>
      ))}
    </span>
  ) : null;

/** „Passt die Gliederung?“ — die gelesenen Kapitel mit Wortlaut, Quelle und Wertung. Kein Editor. */
export function TenderOutlineReview({ outline }: { outline: TenderConceptOutline }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-2" data-testid="tender-outline-review">
      {outline.chapters.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t("tenderBoard.panel.concept.outlineEmpty")}</p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-[11px] uppercase tracking-wide text-muted-foreground">
              <th className="w-6 py-1 font-semibold">#</th>
              <th className="py-1 font-semibold">{t("tenderBoard.panel.concept.outlineChapter")}</th>
              <th className="w-24 py-1 text-right font-semibold">{t("tenderBoard.panel.concept.outlineEvaluation")}</th>
            </tr>
          </thead>
          <tbody>
            {outline.chapters.map((chapter, index) => (
              <tr key={index} className="border-b border-border align-top last:border-0">
                <td className="py-2 tabular-nums text-muted-foreground">{index + 1}</td>
                <td className="py-2 pr-2">
                  <div className="font-medium">{chapter.heading}</div>
                  <div className="mt-0.5 text-xs text-muted-foreground">
                    „{chapter.requirement.length > 240 ? `${chapter.requirement.slice(0, 240)} …` : chapter.requirement}“
                  </div>
                  <SourceChips sources={chapter.sources} />
                </td>
                <td className="py-2 text-right text-xs text-muted-foreground">{chapter.evaluation ?? "–"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {outline.unread.length > 0 && (
        <p className="text-xs text-amber-800">
          {t("tenderBoard.panel.concept.unread", { files: outline.unread.join(", ") })}
        </p>
      )}
    </div>
  );
}

/** „In Word öffnen“ (nur SharePoint) und „.docx laden“. */
export function ConceptDocumentActions({ document }: { document: TenderConceptDocument }) {
  const { t } = useTranslation();
  const [authentication] = useAtom(authenticationAtom) as any;
  const [error, setError] = useState<string | null>(null);
  const file = document.documents[0];
  if (!file) return null;

  const download = async () => {
    setError(null);
    try {
      const { blob, filename } = await userAPI.getDocumentByFileId(authentication.customer, file.fileId, "download", file.name);
      const link = window.document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = filename || file.name;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (err: any) {
      setError(err?.message ?? String(err));
    }
  };

  return (
    <div className="space-y-1">
      <div className="flex flex-wrap gap-2">
        {file.webUrl && (
          <Button asChild className="gap-1.5">
            <a href={file.webUrl} target="_blank" rel="noopener noreferrer">
              <ExternalLink size={14} aria-hidden /> {t("tenderBoard.panel.concept.openInWord")}
            </a>
          </Button>
        )}
        <Button variant={file.webUrl ? "outline" : "default"} className="gap-1.5" onClick={download}>
          <Download size={14} aria-hidden /> {t("tenderBoard.panel.concept.download")}
        </Button>
      </div>
      {error && <p className="text-xs text-red-700">{error}</p>}
    </div>
  );
}

/** Der Kapiteltext mit gelben Platzhaltern; `active` ist der markierte. Bewusst kein Markdown-Parser. */
function ChapterPreview({ text, active }: { text: string; active: number }) {
  let counter = -1;
  const inline = (line: string) =>
    line.split(PLACEHOLDER).map((part, i) => {
      if (!/^\{\{.+\}\}$/.test(part)) return <Fragment key={i}>{part.replace(/\*\*/g, "")}</Fragment>;
      counter += 1;
      return (
        <mark
          key={i}
          data-placeholder-index={counter}
          className={`rounded px-0.5 ${counter === active ? "bg-yellow-300 ring-2 ring-yellow-600" : "bg-yellow-200"}`}
        >
          {part}
        </mark>
      );
    });
  return (
    <div className="space-y-2 text-sm leading-relaxed">
      {text.split(/\n{2,}/).map((block, index) => {
        const lines = block.split("\n");
        if (lines.every((line) => /^\s*[-*] /.test(line))) {
          return (
            <ul key={index} className="ml-5 list-disc space-y-1">
              {lines.map((line, i) => (
                <li key={i}>{inline(line.replace(/^\s*[-*] /, ""))}</li>
              ))}
            </ul>
          );
        }
        if (block.startsWith(">")) {
          return (
            <blockquote key={index} className="border-l-2 border-border pl-3 text-muted-foreground">
              {inline(block.replace(/^>\s?/gm, ""))}
            </blockquote>
          );
        }
        if (/^\*[^*].*\*$/.test(block.trim())) {
          return (
            <p key={index} className="text-xs font-semibold uppercase tracking-wide text-amber-800">
              {block.trim().slice(1, -1)}
            </p>
          );
        }
        return <p key={index}>{inline(block)}</p>;
      })}
    </div>
  );
}

const STATUS_CLASS: Record<string, string> = {
  fertig: "border-emerald-300 bg-emerald-50 text-emerald-900",
  platzhalter: "border-yellow-300 bg-yellow-50 text-yellow-900",
  geruest: "border-amber-300 bg-amber-50 text-amber-900",
};

/** Reiter „Konzept“. */
export function TenderPanelConceptTab({ concept }: { concept: TenderPanelConcept | null | undefined }) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<number | null>(null);
  const [active, setActive] = useState(0);
  const previewRef = useRef<HTMLDivElement>(null);
  const document = concept?.document ?? null;
  const chapters = useMemo(() => document?.chapters ?? [], [document]);

  useEffect(() => {
    previewRef.current?.querySelector(`[data-placeholder-index="${active}"]`)?.scrollIntoView?.({ block: "center" });
  }, [active, selected]);

  if (!document) {
    return (
      <div className="space-y-4" data-testid="tender-panel-concept">
        {concept?.outline ? (
          <>
            <p className="text-sm text-muted-foreground">
              {t("tenderBoard.panel.concept.waitingForOutline", { count: concept.outline.chapters.length })}
            </p>
            <TenderOutlineReview outline={concept.outline} />
          </>
        ) : (
          <p className="text-sm text-muted-foreground">{t("tenderBoard.panel.concept.notStarted")}</p>
        )}
      </div>
    );
  }

  // Deutsche Zeit wie im übrigen Panel — in Browserzeit stand „23.10.2026, 16:57" für 24.10., 01:57
  // (Prod-Test 24.10.2026, Browser in US-Zeit).
  const written = new Date(document.writtenAt).toLocaleString("de-DE", {
    timeZone: "Europe/Berlin",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
  const file = document.documents[0];

  /** Die Position eines Platzhalters über alle Kapitel — „1 von 3 · gesamt 11“. */
  const before = (index: number) => chapters.slice(0, index).reduce((sum, chapter) => sum + chapter.openPlaceholders, 0);
  const nextPlaceholder = (direction: 1 | -1) => {
    if (selected === null) return;
    const own = chapters[selected].openPlaceholders;
    const target = active + direction;
    if (target >= 0 && target < own) return setActive(target);
    // Am Rand: zum nächsten bzw. vorigen Kapitel mit offenen Platzhaltern.
    for (let i = selected + direction; i >= 0 && i < chapters.length; i += direction) {
      if (chapters[i].openPlaceholders > 0) {
        setSelected(i);
        setActive(direction === 1 ? 0 : chapters[i].openPlaceholders - 1);
        return;
      }
    }
  };

  if (selected !== null) {
    const chapter = chapters[selected];
    return (
      <div className="space-y-4" data-testid="tender-panel-concept-chapter">
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <Button variant="ghost" size="sm" className="h-8 gap-1 px-2" onClick={() => setSelected(null)}>
            <ChevronLeft size={14} aria-hidden /> {t("tenderBoard.panel.concept.allChapters")}
          </Button>
          <span className="flex-1 text-center text-xs text-muted-foreground">
            {t("tenderBoard.panel.concept.chapterOf", { index: selected + 1, total: chapters.length, heading: chapter.heading })}
          </span>
          <Button variant="ghost" size="sm" className="h-8 px-2" disabled={selected === 0} onClick={() => { setSelected(selected - 1); setActive(0); }}>
            <ChevronLeft size={14} aria-hidden />
          </Button>
          <Button variant="ghost" size="sm" className="h-8 px-2" disabled={selected === chapters.length - 1} onClick={() => { setSelected(selected + 1); setActive(0); }}>
            <ChevronRight size={14} aria-hidden />
          </Button>
        </div>
        <div className="flex flex-wrap items-center gap-2 rounded-md border border-yellow-300 bg-yellow-50 px-3 py-2 text-sm">
          <span className="font-medium" data-testid="tender-concept-placeholder-count">
            {chapter.openPlaceholders
              ? t("tenderBoard.panel.concept.placeholderPosition", {
                  index: active + 1,
                  count: chapter.openPlaceholders,
                  position: before(selected) + active + 1,
                  total: document.openPlaceholders,
                })
              : t("tenderBoard.panel.concept.noPlaceholders", { total: document.openPlaceholders })}
          </span>
          <span className="flex-1" />
          <Button variant="outline" size="sm" className="h-7" disabled={document.openPlaceholders === 0} onClick={() => nextPlaceholder(-1)}>
            {t("tenderBoard.panel.concept.previousPlaceholder")}
          </Button>
          <Button size="sm" className="h-7" disabled={document.openPlaceholders === 0} onClick={() => nextPlaceholder(1)}>
            {t("tenderBoard.panel.concept.nextPlaceholder")}
          </Button>
        </div>
        <article className="rounded-md border border-border p-4" ref={previewRef}>
          <h3 className="mb-3 text-base font-semibold">
            {selected + 1} {chapter.heading}
          </h3>
          <ChapterPreview text={chapter.text} active={active} />
        </article>
        <p className="text-xs text-muted-foreground">{t("tenderBoard.panel.concept.previewNote", { written })}</p>
        <ConceptDocumentActions document={document} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="tender-panel-concept">
      <section className="rounded-md border border-border p-4">
        <div className="flex items-start gap-3">
          <FileText size={20} className="mt-0.5 shrink-0 text-muted-foreground" aria-hidden />
          <div className="min-w-0 flex-1">
            <div className="truncate font-medium">{file?.name ?? t("tenderBoard.panel.concept.noFile")}</div>
            <div className="text-xs text-muted-foreground">
              {t("tenderBoard.panel.concept.documentLine", {
                written,
                company: document.company,
                chapters: chapters.length,
                open: document.openPlaceholders,
              })}
            </div>
          </div>
        </div>
        <div className="mt-3">
          <ConceptDocumentActions document={document} />
        </div>
      </section>

      <section>
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {t("tenderBoard.panel.concept.chapters")}
        </h3>
        <ol className="divide-y divide-border rounded-md border border-border">
          {chapters.map((chapter, index) => (
            <li key={index} className="flex items-center gap-3 px-3 py-2 text-sm">
              <span className="w-5 tabular-nums text-muted-foreground">{index + 1}</span>
              <span className="min-w-0 flex-1 truncate">{chapter.heading}</span>
              <span className={`shrink-0 rounded border px-1.5 py-0.5 text-[11px] font-medium ${STATUS_CLASS[chapter.status] ?? ""}`}>
                {chapter.status === "fertig"
                  ? t("tenderBoard.panel.concept.statusDone")
                  : chapter.status === "geruest"
                    ? t("tenderBoard.panel.concept.statusScaffold", { count: chapter.openPlaceholders })
                    : t("tenderBoard.panel.concept.statusOpen", { count: chapter.openPlaceholders })}
              </span>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 shrink-0"
                onClick={() => {
                  setSelected(index);
                  setActive(0);
                }}
              >
                {t("tenderBoard.panel.concept.view")}
              </Button>
            </li>
          ))}
        </ol>
      </section>

      {document.notes.length > 0 && (
        <section>
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {t("tenderBoard.panel.concept.notes")}
          </h3>
          <ul className="ml-4 list-disc space-y-1 text-sm">
            {document.notes.map((note, index) => (
              <li key={index}>{note.replace(/\*\*/g, "")}</li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
